// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// search.ts — the opponent. For a two-player perfect-information abstract the bot IS the
// single-player game, so a one-ply greedy mover was never an option: it would make the whole
// design feel pointless, which is the most likely way a game of this shape fails.
//
// THE BRANCHING FACTOR IS THE WHOLE PROBLEM, and it is worth being concrete. On a 9x9 common there
// are 2 * 8 * 8 = 128 hedge slots plus up to 5 pawn steps, so a naive root has ~133 moves and a
// naive depth 3 is ~2.4M nodes — each of which would need a BFS. That is hopeless on a phone.
//
// So candidate hedges are PRUNED to the ones that could possibly matter (`candidateHedges`): a
// hedge is only considered if it severs a link on somebody's current shortest route, or extends a
// hedge already on the board. Everything else is a move that changes no distance this ply and can
// only be a waiting move. That cuts the branching factor to roughly 10-25 and makes depth 3-4
// genuinely reachable inside a 500ms budget, which is what the difficulty ladder spends.
//
// The search is negamax with alpha-beta over TWO runners. A 3-4 player race is not searched at all
// — minimax does not mean anything with three sides — those bots use the same evaluation at one
// ply, which is honest and plenty for a party mode where bots only backfill empty seats.

import {
  atGoal,
  distance,
  hedgeFits,
  hedgeLegal,
  hedgeOf,
  hedgesLeft,
  open,
  pawnMoves,
  play,
  postOf,
  sever,
  unsever,
  FLAT,
  UPRIGHT,
  type Cell,
  type Hedge,
  type Move,
  type State,
} from './rules';

export interface Skill {
  readonly id: string;
  readonly name: string;
  /** Nominal search depth in plies. 1 means "no search at all", which is the point of Rambler. */
  readonly depth: number;
  /** Milliseconds the move may take. Iterative deepening returns the best line found so far. */
  readonly ms: number;
  /** Centi-step jitter added to root scores so the same position is not always answered the same
   *  way — and, at the low tiers, so it makes real mistakes rather than perfect small ones. */
  readonly noise: number;
}

export const SKILLS: Record<string, Skill> = {
  rambler: { id: 'rambler', name: 'Rambler', depth: 1, ms: 60, noise: 130 },
  poacher: { id: 'poacher', name: 'Poacher', depth: 2, ms: 220, noise: 34 },
  keeper: { id: 'keeper', name: 'Gamekeeper', depth: 4, ms: 550, noise: 0 },
};

export const SKILL_IDS = ['rambler', 'poacher', 'keeper'] as const;

export function skillOf(id: unknown): Skill {
  if (typeof id === 'string' && Object.hasOwn(SKILLS, id)) return SKILLS[id];
  return SKILLS.poacher;
}

const WIN = 100_000;
/** A step of progress is worth this much; a hedge in hand is worth a fraction of a step. */
const W_STEP = 100;
const W_HEDGE = 7;

/**
 * One cell-to-cell link on a shortest route, as the pair of cells it joins. Used to ask "which
 * hedges could sever this?" without the caller knowing anything about post geometry.
 */
interface Link {
  from: Cell;
  to: Cell;
}

/**
 * The cells of ONE shortest route to the goal edge, as links. BFS with parent tracking — the same
 * search `distance` runs, keeping the tree so the route can be walked back.
 */
export function shortestLinks(state: State, from: Cell, goal: Parameters<typeof atGoal>[1], out: Link[] = []): Link[] {
  out.length = 0;
  const { size, walls } = state;
  const n = size * size;
  const prev = new Int16Array(n).fill(-1);
  const seen = new Uint8Array(n);
  const queue = new Int16Array(n);
  let head = 0;
  let tail = 0;
  queue[tail++] = from;
  seen[from] = 1;
  let end = atGoal(size, goal, from) ? from : -1;

  while (head < tail && end < 0) {
    const cell = queue[head++];
    for (let d = 0; d < 4; d++) {
      const next = open(size, walls, cell, d);
      if (next < 0 || seen[next]) continue;
      seen[next] = 1;
      prev[next] = cell;
      if (atGoal(size, goal, next)) {
        end = next;
        break;
      }
      queue[tail++] = next;
    }
  }
  if (end < 0) return out;
  for (let c = end; prev[c] >= 0; c = prev[c]) out.push({ from: prev[c], to: c });
  return out;
}

/** The (at most two) hedges that would sever the link between two orthogonally adjacent cells. */
export function hedgesSevering(size: number, a: Cell, b: Cell, out: Hedge[] = []): Hedge[] {
  out.length = 0;
  const span = size - 1;
  const ar = (a / size) | 0;
  const ac = a % size;
  const br = (b / size) | 0;
  const bc = b % size;

  if (ac === bc) {
    // vertical link — a FLAT hedge across it, anchored on either post to its left or right
    const r = Math.min(ar, br);
    for (const c of [ac - 1, ac]) {
      if (r >= 0 && r < span && c >= 0 && c < span) out.push(hedgeOf(r * span + c, FLAT));
    }
  } else {
    const c = Math.min(ac, bc);
    for (const r of [ar - 1, ar]) {
      if (r >= 0 && r < span && c >= 0 && c < span) out.push(hedgeOf(r * span + c, UPRIGHT));
    }
  }
  return out;
}

/**
 * The hedges worth considering this ply. Everything else on the lattice is a move that changes
 * nobody's distance and so cannot be better than simply walking — the one exception being a hedge
 * that extends an existing one into a future trap, which is why placed hedges seed candidates too.
 */
export function candidateHedges(state: State, who: number): Hedge[] {
  const out = new Set<Hedge>();
  if (hedgesLeft(state, who) <= 0) return [];
  const span = state.size - 1;
  const links: Link[] = [];
  const sev: Hedge[] = [];

  for (let i = 0; i < state.runners.length; i++) {
    const r = state.runners[i];
    shortestLinks(state, r.at, r.goal, links);
    // Only the first few steps of a route matter tactically; hedging the far end of a long route
    // is a move the opponent simply walks around.
    const take = Math.min(links.length, 6);
    for (let k = 0; k < take; k++) {
      hedgesSevering(state.size, links[k].from, links[k].to, sev);
      for (const h of sev) out.add(h);
    }
  }

  // Extensions of what is already planted: the second half of a real trap.
  for (const placed of state.placed) {
    const p = postOf(placed);
    const pr = (p / span) | 0;
    const pc = p % span;
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        const r = pr + dr;
        const c = pc + dc;
        if (r < 0 || c < 0 || r >= span || c >= span) continue;
        out.add(hedgeOf(r * span + c, FLAT));
        out.add(hedgeOf(r * span + c, UPRIGHT));
      }
    }
  }

  const legal: Hedge[] = [];
  for (const h of out) {
    if (hedgeFits(state, h) && hedgeLegal(state, h)) legal.push(h);
  }
  return legal;
}

/** Every legal move for the runner to move, hedges already pruned to the candidates. */
export function moves(state: State, who: number): Move[] {
  const out: Move[] = [];
  for (const to of pawnMoves(state, who)) out.push({ kind: 'step', to });
  for (const hedge of candidateHedges(state, who)) out.push({ kind: 'hedge', hedge });
  return out;
}

/**
 * How good this position is for `me`, in centi-steps. The whole of the evaluation is the two
 * shortest routes — which is the standard strong evaluation for this family of game and, unusually
 * for a heuristic, is very close to the truth: the race IS the difference of the two distances.
 */
export function evaluate(state: State, me: number, scratch?: Int16Array): number {
  const size = state.size;
  const mine = state.runners[me];
  const myD = distance(size, state.walls, mine.at, mine.goal, scratch);
  if (myD === 0) return WIN;

  let bestOther = Infinity;
  let otherHedges = 0;
  for (let i = 0; i < state.runners.length; i++) {
    if (i === me) continue;
    const r = state.runners[i];
    const d = distance(size, state.walls, r.at, r.goal, scratch);
    if (d === 0) return -WIN;
    if (d < bestOther) bestOther = d;
    otherHedges += hedgesLeft(state, i);
  }
  const rivals = state.runners.length - 1;
  return (bestOther - myD) * W_STEP + (hedgesLeft(state, me) - otherHedges / rivals) * W_HEDGE;
}

/** A cheap clone. The search makes and unmakes through this rather than mutating shared state:
 *  hedges are trivially unmade, but a pawn step plus a possible win flag is fiddlier, and at these
 *  node counts the copy is not what costs. */
function clone(state: State): State {
  return {
    size: state.size,
    runners: state.runners.map((r) => ({ ...r })),
    pool: state.pool,
    turn: state.turn,
    placed: state.placed.slice(),
    walls: state.walls.slice(),
    ply: state.ply,
    winner: state.winner,
  };
}

interface Ctx {
  deadline: number;
  nodes: number;
  aborted: boolean;
  scratch: Int16Array;
}

function negamax(state: State, me: number, depth: number, alpha: number, beta: number, ctx: Ctx): number {
  ctx.nodes++;
  if ((ctx.nodes & 63) === 0 && Date.now() > ctx.deadline) {
    ctx.aborted = true;
    return 0;
  }
  if (state.winner >= 0) return state.winner === me ? WIN - state.ply : -WIN + state.ply;
  if (depth <= 0) return evaluate(state, me, ctx.scratch);

  const who = state.turn;
  const mover = who === me;
  const list = moves(state, who);
  if (list.length === 0) return evaluate(state, me, ctx.scratch);

  // Order: walking first (it is the move that usually matters), then hedges. Cheap and it gets
  // most of the alpha-beta benefit without a full scoring pass at every node.
  list.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'step' ? -1 : 1));

  let best = mover ? -Infinity : Infinity;
  for (const m of list) {
    const next = clone(state);
    if (!play(next, m)) continue;
    const score = negamax(next, me, depth - 1, alpha, beta, ctx);
    if (ctx.aborted) return best === Infinity || best === -Infinity ? 0 : best;
    if (mover) {
      if (score > best) best = score;
      if (best > alpha) alpha = best;
    } else {
      if (score < best) best = score;
      if (best < beta) beta = best;
    }
    if (alpha >= beta) break;
  }
  return best === Infinity || best === -Infinity ? evaluate(state, me, ctx.scratch) : best;
}

export interface Chosen {
  move: Move;
  score: number;
  depth: number;
  nodes: number;
  ms: number;
}

/**
 * Pick a move. Iterative deepening against a wall-clock deadline, so the cost is bounded by TIME
 * rather than by depth — the same call is safe on a slow phone and simply searches less deep.
 * `rand` is injected so a simulation is reproducible from a seed.
 */
export function chooseMove(state: State, skill: Skill, rand: () => number = Math.random): Chosen | null {
  const me = state.turn;
  const started = Date.now();
  const root = moves(state, me);
  if (root.length === 0) return null;

  const ctx: Ctx = {
    deadline: started + skill.ms,
    nodes: 0,
    aborted: false,
    scratch: new Int16Array(state.size * state.size),
  };

  let best: Move = root[0];
  let bestScore = -Infinity;
  let reached = 0;

  for (let depth = 1; depth <= skill.depth; depth++) {
    let localBest: Move | null = null;
    let localScore = -Infinity;
    for (const m of root) {
      const next = clone(state);
      if (!play(next, m)) continue;
      let score = next.winner === me ? WIN - next.ply : negamax(next, me, depth - 1, -Infinity, Infinity, ctx);
      if (ctx.aborted) break;
      score += skill.noise > 0 ? (rand() - 0.5) * 2 * skill.noise : 0;
      if (score > localScore) {
        localScore = score;
        localBest = m;
      }
    }
    if (ctx.aborted) break;
    if (localBest) {
      best = localBest;
      bestScore = localScore;
      reached = depth;
    }
    if (bestScore >= WIN - 1000) break; // a forced win — no point looking deeper
  }

  return { move: best, score: bestScore, depth: reached, nodes: ctx.nodes, ms: Date.now() - started };
}

/** Used by the balance sim as the CONTROL arm: walk the shortest route, never hedge. Beating this
 *  decisively is the minimum bar for the shipped bot to be worth calling a bot. */
export function greedyMove(state: State, rand: () => number = Math.random): Move | null {
  const me = state.turn;
  const steps = pawnMoves(state, me);
  if (steps.length === 0) return null;
  const runner = state.runners[me];
  let best = steps[0];
  let bestD = Infinity;
  for (const to of steps) {
    const saved = runner.at;
    runner.at = to;
    const d = atGoal(state.size, runner.goal, to) ? -1 : distance(state.size, state.walls, to, runner.goal);
    runner.at = saved;
    if (d >= 0 || d === -1) {
      const score = d === -1 ? -1 : d;
      if (score < bestD || (score === bestD && rand() < 0.3)) {
        bestD = score;
        best = to;
      }
    }
  }
  return { kind: 'step', to: best };
}

/** Kept exported so the mechanism audit can assert the search never proposes an illegal hedge. */
export { sever, unsever };
